import { PopoverAnchorRef, PopoverOrigin, PopoverRef, PopoverStyle } from '../model/types';

const computeAnchorOriginTop = (anchorRect: DOMRect, anchorOrigin: PopoverOrigin) => {
  const { vertical } = anchorOrigin;
  const { height: anchorHeight, top: anchorTop } = anchorRect;

  if (vertical === 'top') {
    return anchorTop;
  }

  if (vertical === 'center') {
    return anchorTop + anchorHeight / 2;
  }

  return anchorTop + anchorHeight;
};

const computeAnchorOriginLeft = (anchorRect: DOMRect, anchorOrigin: PopoverOrigin) => {
  const { horizontal } = anchorOrigin;
  const { width: anchorWidth, left: anchorLeft } = anchorRect;

  if (horizontal === 'left') {
    return anchorLeft;
  }

  if (horizontal === 'center') {
    return anchorLeft + anchorWidth / 2;
  }

  return anchorLeft + anchorWidth;
};

const computePopoverTopOffset = (popoverHeight: number, transformOrigin: PopoverOrigin) => {
  const { vertical } = transformOrigin;

  if (vertical === 'top') {
    return 0;
  }

  if (vertical === 'center') {
    return -(popoverHeight / 2);
  }

  return -popoverHeight;
};

const computePopoverLeftOffset = (popoverWidth: number, transformOrigin: PopoverOrigin) => {
  const { horizontal } = transformOrigin;

  if (horizontal === 'left') {
    return 0;
  }

  if (horizontal === 'center') {
    return -(popoverWidth / 2);
  }

  return -popoverWidth;
};

const px = (value: number) => {
  return `${value}px`;
};

const adjustTop = (top: number, popoverHeight: number) => {
  return Math.min(Math.max(top, 0), window.innerHeight - popoverHeight);
};

const adjustLeft = (left: number, popoverWidth: number) => {
  return Math.min(Math.max(left, 0), window.innerWidth - popoverWidth);
};

export const computePopoverStyle = (
  anchorRef: PopoverAnchorRef,
  popoverRef: PopoverRef,
  anchorOrigin: PopoverOrigin,
  transformOrigin: PopoverOrigin,
  preventOverflow: boolean
): PopoverStyle => {
  const anchorRect = anchorRef.current?.getBoundingClientRect();
  const popover = popoverRef.current;

  if (!anchorRect || !popover) {
    return {};
  }

  const popoverWidth = popover.offsetWidth;
  const popoverHeight = popover.offsetHeight;
  const anchorOriginTop = computeAnchorOriginTop(anchorRect, anchorOrigin);
  const topOffset = computePopoverTopOffset(popoverHeight, transformOrigin);
  const top = anchorOriginTop + topOffset;
  const anchorOriginLeft = computeAnchorOriginLeft(anchorRect, anchorOrigin);
  const leftOffset = computePopoverLeftOffset(popoverWidth, transformOrigin);
  const left = anchorOriginLeft + leftOffset;

  return {
    top: px(preventOverflow ? adjustTop(top, popoverHeight) : top),
    left: px(preventOverflow ? adjustLeft(left, popoverWidth) : left),
    'transform-origin': `${transformOrigin.horizontal} ${transformOrigin.vertical}`,
  };
};
